import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  GeneralDialog,
  InputWithLabel,
  RadioGroup,
  TextAreaWithLabel,
} from "@ptsd/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog, Form, Text, View, YStack } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";

const validationErrors = {
  subject: "Будь ласка, оберіть тему повідомлення",
  email: "Некоректний формат email ",
  message: "Повідомлення має містити від 1 до 1000 символів.",
};

const schema = z.object({
  subject: z.string().min(1, {
    message: validationErrors.subject,
  }),
  email: z.string().email({
    message: validationErrors.email,
  }),
  message: z
    .string()
    .min(1, {
      message: validationErrors.message,
    })
    .max(1000, {
      message: validationErrors.message,
    }),
});

export const ContactUsDialog: React.FC<{ trigger: React.ReactNode }> = ({
  trigger,
}) => {
  const { t } = useTranslation("blocks");
  const [sent, setSent] = useState(false);

  const {
    control,
    setError,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      subject: "",
      email: "",
      message: "",
    },
  });

  const radioGroupItems = t("contact-us.contactForm.radio-group.items", {
    returnObjects: true,
  });

  const { mutate, isLoading } = api.messages.contactUs.useMutation({
    onSuccess: () => {
      setSent(true);
      reset();
    },
    onError: (error) => {
      if (error.data?.code === "INTERNAL_SERVER_ERROR") {
        setError("root", {
          type: "manual",
          message: t(
            "contact-us.contactForm.generalErrorMessages.failed-to-send",
          ),
        });
      }
    },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    mutate(data);
  };

  const disabled =
    isLoading || sent || !isDirty || Object.keys(errors).length > 0;

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) {
          setSent(false);
          reset();
        }
      }}
    >
      <Dialog.Trigger data-id="invite-to-group-dialog-trigger">
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          zIndex={0}
          key={"add-group-dialog-overlay"}
          style={{
            backgroundColor: "#6A696966",
            backdropFilter: "blur(10px)",
          }}
        />
        <GeneralDialog.Content width={"100%"} maxWidth={600}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <View paddingHorizontal={"$10"} gap={"$3"} paddingVertical={"$3"}>
              <Text
                fontFamily={"$subtitle"}
                fontWeight={"$6"}
                fontSize={"$6"}
                lineHeight={"$6"}
                textAlign="center"
              >
                {t("contact-us.title")}
              </Text>

              <View gap={"$4"}>
                <Controller
                  name="subject"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <YStack>
                        <Text
                          paddingLeft={"$1"}
                          fontFamily={"$body"}
                          fontSize={"$2"}
                          lineHeight={"$6"}
                          fontWeight={"$6"}
                        >
                          {t("contact-us.contactForm.radio-group.title")}
                        </Text>
                        <RadioGroup.Container
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          {radioGroupItems.map((item, index) => (
                            <RadioGroup.Item
                              key={item}
                              size="sm"
                              variant="plain"
                              value={item}
                              index={index}
                              text={item}
                            />
                          ))}
                        </RadioGroup.Container>
                        {fieldState.invalid && (
                          <Text
                            paddingLeft={"$1"}
                            paddingTop={"$1"}
                            color={"$error-500"}
                            fontFamily={"$subtitle"}
                            fontSize={"$1"}
                            lineHeight={"$1"}
                            fontWeight={"$4"}
                          >
                            {fieldState.error?.message}
                          </Text>
                        )}
                      </YStack>
                    );
                  }}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputWithLabel
                      variant="inline"
                      size="md"
                      label={t("contact-us.contactForm.formFields.email.label")}
                      placeholder={t(
                        "contact-us.contactForm.formFields.email.placeholder",
                      )}
                      {...field}
                      isError={fieldState.invalid}
                      error={fieldState.error?.message}
                    />
                  )}
                />

                <Controller
                  name="message"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextAreaWithLabel
                      height={120}
                      font="md"
                      label={t(
                        "contact-us.contactForm.formFields.message.label",
                      )}
                      placeholder={t(
                        "contact-us.contactForm.formFields.message.placeholder",
                      )}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </View>
              {errors.root?.message && (
                <Text
                  color={"$error-500"}
                  fontFamily={"$subtitle"}
                  fontSize={"$1"}
                  lineHeight={"$1"}
                  fontWeight={"$4"}
                >
                  {errors.root.message}
                </Text>
              )}
            </View>
            <GeneralDialog.ControllArea
              alignItems="center"
              paddingVertical={"$3"}
              gap={"$2"}
            >
              <Form.Trigger asChild>
                <Button
                  variant="primary"
                  rounded="lg"
                  size="md"
                  height={"$7"}
                  width={"100%"}
                  maxWidth={223}
                  disabled={disabled}
                >
                  {isLoading
                    ? t("contact-us.contactForm.button.loading")
                    : sent
                      ? t("contact-us.contactForm.button.sent")
                      : t("contact-us.contactForm.button.send")}
                </Button>
              </Form.Trigger>
              <GeneralDialog.Close>
                {t("contact-us.contactForm.button.close")}
              </GeneralDialog.Close>
            </GeneralDialog.ControllArea>
          </Form>
        </GeneralDialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
